"use client"

import React, { useEffect, useMemo, useState } from "react"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Loader2 } from "lucide-react"
import { useSchemaPaths } from "@/hooks/use-schema-paths"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

type ItemMode = "all" | "first" | "condition"
type ConditionOperator = "is" | "isNot" | "isEmpty" | "isNotEmpty"

/** Operator labels are looked up at the render site, so this module stays locale-free */
const OPERATOR_KEYS: Record<ConditionOperator, TranslationKey> = {
  is: "arrayRefiner.operators.is",
  isNot: "arrayRefiner.operators.isNot",
  isEmpty: "arrayRefiner.operators.isEmpty",
  isNotEmpty: "arrayRefiner.operators.isNotEmpty",
}

/** Strips item selectors back to bare "[]": "[0]" and "[?(...)]" (with optional "[0]") */
function normalizeToBasePath(path: string): string {
  return path
    .replace(/\[\?\(.*?\)\]/g, "[]")
    .replace(/\[0\]/g, "[]")
    .replace(/\[\]\[\]/g, "[]")
}

export interface ArrayPathRefinerProps {
  /** The ATS entity type whose live schema supplies condition fields */
  entityType: string
  /** The current source field path */
  path: string | null
  onChange: (path: string) => void
  /**
   * "many" (default): the path may map several values — offers All items, First
   * item, and condition with a first-match toggle. "single": the path must
   * resolve to one value (e.g. related-entity reference IDs, resolved first-match
   * server-side) — offers First item and condition only.
   */
  selection?: "many" | "single"
}

/**
 * Companion to a source-field picker for paths that traverse an array
 * (e.g. job_profiles[].title): pick all items, the first item, or items
 * matching a condition — generating the JSONPath filter expressions the
 * mapping engine already understands ([0], [?(@.field == value)],
 * [?(...)][0]) without exposing the syntax. Mirrors the Reference ID
 * builder's row picker, plus an "all matches" option since field mappings
 * can legitimately map arrays of values.
 */
export function ArrayPathRefiner({ entityType, path, onChange, selection = "many" }: ArrayPathRefinerProps) {
  const { t } = useTranslation()
  const singleValue = selection === "single"

  const [mode, setMode] = useState<ItemMode>(singleValue ? "first" : "all")
  const [firstMatchOnly, setFirstMatchOnly] = useState(false)
  const [conditionField, setConditionField] = useState("")
  const [conditionOperator, setConditionOperator] = useState<ConditionOperator>("is")
  const [conditionValue, setConditionValue] = useState("")

  const basePath = path ? normalizeToBasePath(path) : ""
  const arrayPrefix = basePath.includes("[]") ? basePath.slice(0, basePath.indexOf("[]")) : null

  // Reflect the existing path's shape when it changes from outside, without trying
  // to re-parse condition details — a hand-written filter stays intact until the
  // user touches a control here
  useEffect(() => {
    if (!path) return
    if (/\[\?\(.*?\)\]\[0\]/.test(path)) {
      setMode("condition")
      setFirstMatchOnly(true)
    } else if (path.includes("[?(")) {
      setMode("condition")
      setFirstMatchOnly(false)
    } else if (path.includes("[0]")) {
      setMode("first")
    } else {
      // Bare "[]" resolves first-match server-side in single-value contexts
      setMode(singleValue ? "first" : "all")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path])

  // The entity's live schema; the array's element node ("commissions[]") is expanded
  // as soon as it is known, so the condition dropdown offers the element's own fields
  // even when the parent schema only lists "[].id"
  const schema = useSchemaPaths(arrayPrefix ? entityType : null, { autoLoad: true })
  const schemaFields = schema.fields ?? []
  const isLoadingSchema = schema.isLoading || schema.expandingPath !== null

  useEffect(() => {
    if (!arrayPrefix) return
    const elementNode = `${arrayPrefix}[]`
    const node = schemaFields.find((f) => f.path === elementNode)
    if (node?.isExpandable && !schema.expandedPaths.has(elementNode) && schema.expandingPath === null) {
      void schema.expand(elementNode)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemaFields, arrayPrefix])

  // Fields of the array element, for the condition dropdown (nested arrays excluded)
  const conditionFieldOptions = useMemo(() => {
    if (!arrayPrefix) return []
    const elementPrefix = `${arrayPrefix}[].`
    return schemaFields
      .filter((f) => f.path.startsWith(elementPrefix) && !f.isExpandable)
      .map((f) => ({ relativePath: f.path.slice(elementPrefix.length), dataType: f.dataType }))
      .filter((f) => !f.relativePath.includes("[]"))
  }, [schemaFields, arrayPrefix])

  if (!arrayPrefix) return null

  /** Quotes the value per the field's discovered data type (numbers/booleans unquoted) */
  const encodeConditionValue = (field: string, value: string): string => {
    const dataType = conditionFieldOptions.find((f) => f.relativePath === field)?.dataType
    if (dataType === "number" || dataType === "boolean") {
      return value || "null"
    }
    return `'${value.replace(/'/g, "\\'")}'`
  }

  const buildPath = (
    nextMode: ItemMode,
    nextFirstOnly: boolean,
    field: string,
    operator: ConditionOperator,
    value: string
  ): string => {
    let selector = "[]"
    if (nextMode === "first") {
      selector = "[0]"
    } else if (nextMode === "condition" && field) {
      const comparison =
        operator === "isEmpty"
          ? "== null"
          : operator === "isNotEmpty"
            ? "!= null"
            : `${operator === "is" ? "==" : "!="} ${encodeConditionValue(field, value)}`
      selector = `[?(@.${field} ${comparison})]${nextFirstOnly ? "[0]" : ""}`
    }

    const splitAt = basePath.indexOf("[]")
    return basePath.slice(0, splitAt) + selector + basePath.slice(splitAt + 2)
  }

  const isConditionIncomplete = (nextMode: ItemMode, field: string, operator: ConditionOperator, value: string) =>
    nextMode === "condition" && (!field || (operator !== "isEmpty" && operator !== "isNotEmpty" && value === ""))

  /**
   * Recomputes and applies the path whenever a control changes. An incomplete
   * condition applies nothing (the source field keeps its current value)
   * instead of silently falling back to all items.
   */
  const apply = (update: {
    mode?: ItemMode
    firstOnly?: boolean
    field?: string
    operator?: ConditionOperator
    value?: string
  }) => {
    const nextMode = update.mode ?? mode
    const nextFirstOnly = update.firstOnly ?? firstMatchOnly
    const field = update.field ?? conditionField
    const operator = update.operator ?? conditionOperator
    const value = update.value ?? conditionValue

    if (update.mode !== undefined) setMode(update.mode)
    if (update.firstOnly !== undefined) setFirstMatchOnly(update.firstOnly)
    if (update.field !== undefined) setConditionField(update.field)
    if (update.operator !== undefined) setConditionOperator(update.operator)
    if (update.value !== undefined) setConditionValue(update.value)

    if (isConditionIncomplete(nextMode, field, operator, value)) return

    onChange(buildPath(nextMode, nextFirstOnly, field, operator, value))
  }

  const needsValue = conditionOperator !== "isEmpty" && conditionOperator !== "isNotEmpty"
  const conditionIncomplete = isConditionIncomplete(mode, conditionField, conditionOperator, conditionValue)

  return (
    <div className="border rounded-md p-3 bg-muted/30 space-y-2">
      <p className="text-xs font-medium">
        <code className="bg-muted px-1 rounded">{arrayPrefix}</code>{" "}
        {t((singleValue ? "arrayRefiner.prompt.single" : "arrayRefiner.prompt.many"))}
        {isLoadingSchema && <Loader2 className="inline h-3 w-3 animate-spin ml-1.5" />}
      </p>
      <RadioGroup
        value={mode}
        onValueChange={(value) => apply({ mode: value as ItemMode })}
        className="flex flex-wrap gap-4"
      >
        {!singleValue && (
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <RadioGroupItem value="all" /> {t("arrayRefiner.modes.all")}
          </label>
        )}
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <RadioGroupItem value="first" /> {t("arrayRefiner.modes.first")}
        </label>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <RadioGroupItem value="condition" disabled={conditionFieldOptions.length === 0} />
          {t((singleValue ? "arrayRefiner.modes.conditionSingle" : "arrayRefiner.modes.conditionMany"))}
        </label>
      </RadioGroup>

      {mode === "condition" && (
        <>
          <div className="flex flex-wrap gap-2 items-center">
            <Select value={conditionField || undefined} onValueChange={(field) => apply({ field })}>
              <SelectTrigger className="w-56 h-8 text-xs">
                <SelectValue placeholder={t("arrayRefiner.placeholders.field")} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {conditionFieldOptions.map((option) => (
                  <SelectItem key={option.relativePath} value={option.relativePath}>
                    {option.relativePath}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={conditionOperator}
              onValueChange={(operator) => apply({ operator: operator as ConditionOperator })}
            >
              <SelectTrigger className="w-32 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(OPERATOR_KEYS) as ConditionOperator[]).map((op) => (
                  <SelectItem key={op} value={op}>
                    {t(OPERATOR_KEYS[op])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {needsValue && (
              <Input
                className="w-44 h-8 text-xs"
                placeholder={t("arrayRefiner.placeholders.value")}
                value={conditionValue}
                onChange={(e) => apply({ value: e.target.value })}
              />
            )}
          </div>
          {!singleValue && (
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <Checkbox
                checked={firstMatchOnly}
                onCheckedChange={(checked) => apply({ firstOnly: checked === true })}
              />
              {t("arrayRefiner.options.firstMatchOnly")}
            </label>
          )}
        </>
      )}

      <p className="text-xs text-muted-foreground">
        {conditionIncomplete ? (
          <span className="text-amber-600">
            {t((needsValue ? "arrayRefiner.incomplete.fieldAndValue" : "arrayRefiner.incomplete.field"))}
          </span>
        ) : (
          <>
            {t("arrayRefiner.labels.path")} <code className="bg-muted px-1 rounded break-all">{path}</code>
          </>
        )}
      </p>
    </div>
  )
}
